import { useEffect, useRef } from "react";
import { PageMeta } from "@/components/PageMeta";
import { useParams, Link, Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ArrowLeft, Loader2 } from "lucide-react";
import { PageContainer } from "@/components/layouts/MainLayout";
import NotFound from "@/pages/NotFound";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { getKnowledgeHubPath, getLastPathSegment } from "@/lib/routeUtils";
import { migratedCanonicalSlug } from "@/lib/localizedSlugMigration";

const HUBSPOT_PORTAL_ID = "143197515";
const HUBSPOT_REGION = "eu1";

/** Map localised URL slugs to the canonical (NL) database slug */
const SLUG_MAP: Record<string, string> = {
  // Templates EN
  'end-of-probation-form': 'einde-proeftijdsgesprek',
  'template-planning-meeting': 'planningsgesprek-doelstellingengesprek',
  'template-1-1-meetings': 'check-in-gesprek-template',
  'performance-review-new-style': 'functioneringsgesprek-nieuwe-stijl',
  // Templates DE
  'vorlage-beurteilungsgesprach': 'functioneringsgesprek-nieuwe-stijl',
  'vorlage-planungsgespraech': 'planningsgesprek-doelstellingengesprek',
  'vorlage-mitarbeitergespraech': 'check-in-gesprek-template',
  // E-guides EN
  'objective-performance-management': 'objectieve-performance-management',
  'high-low-performers': 'low-en-high-performers',
  'employee-engagement-insight': 'realtime-inzicht-in-betrokkenheid',
  'employee-satisfaction-survey': 'learned-engagement-model',
  'modern-hr-cycle': 'moderne-hr-cyclus',
  // E-guides DE
  'objektives-personalmanagement': 'objectieve-performance-management',
  'hr-zyklus': 'moderne-hr-cyclus',
  'vorlage-mitarbeiterumfrage': 'learned-engagement-model',
};

function useHubSpotForm(containerRef: React.RefObject<HTMLDivElement>, formId: string | undefined) {
  useEffect(() => {
    const el = containerRef.current;
    if (!el || !formId) return;
    el.innerHTML = "";

    const frame = document.createElement("div");
    frame.className = "hs-form-frame";
    frame.setAttribute("data-region", HUBSPOT_REGION);
    frame.setAttribute("data-form-id", formId);
    frame.setAttribute("data-portal-id", HUBSPOT_PORTAL_ID);
    el.appendChild(frame);

    const style = document.createElement("style");
    style.textContent = `
      .hs-form-frame fieldset { max-width: none !important; padding: 0 !important; margin: 0 !important; }
      .hs-form-frame .hs-input {
        width: 100% !important;
        box-sizing: border-box !important;
        height: 52px !important;
        border-radius: 9999px !important;
        border: none !important;
        background: #F3F1F5 !important;
        padding: 0 20px !important;
        font-size: 16px !important;
      }
      .hs-form-frame select.hs-input {
        height: 52px !important;
        border-radius: 9999px !important;
        padding: 0 20px !important;
        appearance: auto !important;
      }
      .hs-form-frame textarea.hs-input {
        height: auto !important;
        border-radius: 16px !important;
        padding: 14px 20px !important;
      }
      .hs-form-frame .input { margin-right: 0 !important; }
      .hs-form-frame .hs-form-field { padding: 0 !important; margin: 0 !important; }
      .hs-form-frame form { padding: 0 !important; margin: 0 !important; }
      .hs-form-frame form > div:first-child,
      .hs-form-frame form fieldset:first-of-type {
        display: grid !important;
        grid-template-columns: 1fr !important;
        gap: 16px 0 !important;
      }
      .hs-form-frame .hs-submit { margin-top: 12px; }
      .hs-form-frame .hs-button {
        width: 100% !important;
        border-radius: 9999px !important;
        height: 52px !important;
        font-size: 18px !important;
        font-weight: 600 !important;
      }
      .hs-form-frame label {
        padding-left: 0 !important;
        font-size: 16px !important;
        font-weight: 600 !important;
        margin-bottom: 6px !important;
        display: block !important;
      }
    `;
    el.appendChild(style);

    const script = document.createElement("script");
    script.src = `https://js-${HUBSPOT_REGION}.hsforms.net/forms/embed/${HUBSPOT_PORTAL_ID}.js`;
    script.async = true;
    script.defer = true;
    el.appendChild(script);

    return () => { el.innerHTML = ""; };
  }, [containerRef, formId]);
}

const typeLabels: Record<string, Record<string, string>> = {
  template: { nl: "Template", en: "Template", de: "Vorlage" },
  eguide: { nl: "E-guide", en: "E-guide", de: "E-Guide" },
};

const downloadLabels: Record<string, string> = {
  nl: "Ontvang de gratis PDF",
  en: "Get the free PDF",
  de: "Kostenlose PDF erhalten",
};

const learningTitles: Record<string, Record<string, string>> = {
  template: { nl: "In dit template:", en: "In this template:", de: "In dieser Vorlage:" },
  eguide: { nl: "In deze e-guide:", en: "In this e-guide:", de: "In diesem Leitfaden:" },
};

export default function DownloadableResourceDetail() {
  const { lang, '*': splat } = useParams<{ lang: string; '*': string }>();
  const rawSlug = getLastPathSegment(splat) || '';
  const slug = migratedCanonicalSlug(`/${lang}/${splat || ''}`) || SLUG_MAP[rawSlug] || rawSlug || undefined;
  const { i18n } = useTranslation();
  const formRef = useRef<HTMLDivElement>(null);
  const mobileFormRef = useRef<HTMLDivElement>(null);
  const currentLang = (i18n.language || lang || "nl") as "nl" | "en" | "de";
  const knowledgeHubPath = getKnowledgeHubPath(currentLang);

  const { data: resource, isLoading } = useQuery({
    queryKey: ["hr-resource-detail", slug],
    queryFn: async () => {
      if (!slug) return null;
      const { data, error } = await supabase
        .from("hr_resources")
        .select("*")
        .eq("slug", slug)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!slug,
  });

  const hubspotFormId = resource
    ? ((resource as any)[`hubspot_form_id_${currentLang}`] || (resource as any).hubspot_form_id_nl || null)
    : undefined;

  useHubSpotForm(formRef, hubspotFormId);
  useHubSpotForm(mobileFormRef, hubspotFormId);

  const getLocalized = (field: string) => {
    if (!resource) return "";
    if (currentLang === "en" && resource[`${field}_en` as keyof typeof resource]) return resource[`${field}_en` as keyof typeof resource] as string;
    if (currentLang === "de" && resource[`${field}_de` as keyof typeof resource]) return resource[`${field}_de` as keyof typeof resource] as string;
    return (resource[`${field}_nl` as keyof typeof resource] as string) || "";
  };

  const getLearnings = (): string[] => {
    if (!resource) return [];
    const key = `learnings_${currentLang}` as keyof typeof resource;
    const val = resource[key] || resource.learnings_nl;
    return Array.isArray(val) ? val as string[] : [];
  };

  const getContent = (): string[] => {
    if (!resource) return [];
    const key = `content_${currentLang}` as keyof typeof resource;
    const val = resource[key] || resource.content_nl;
    if (!Array.isArray(val)) return [];
    return val.map((item: any) => {
      if (typeof item === 'string') return item;
      if (item && typeof item === 'object' && item.content) return item.content;
      return String(item);
    });
  };

  if (isLoading) {
    return (
      <section className="pt-[140px] pb-20 px-6">
        <PageContainer>
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        </PageContainer>
      </section>
    );
  }

  if (!resource) {
    return <NotFound />;
  }
  // Redirect to homepage if this template has no content/form for the current language
  const hasLocalizedContent = currentLang === 'nl' || !!hubspotFormId;
  if (!hasLocalizedContent && (resource.type === 'template' || resource.type === 'eguide')) {
    return <Navigate to={`/${lang}/`} replace />;
  }

  const title = getLocalized("title");
  const learnings = getLearnings();
  const contentParagraphs = getContent();
  const resourceType = resource.type || "template";
  const downloadLabel = downloadLabels[currentLang];
  const learningTitle = learningTitles[resourceType]?.[currentLang] || "In dit template:";
  const hasForm = !!hubspotFormId;
  const hasDownload = !!resource.download_url;

  const typeLabel = typeLabels[resourceType]?.[currentLang] || resourceType;
  const metaTitle = title ? `${typeLabel}: ${title} | Learned` : "Resource | Learned";
  const metaDesc = getLocalized("description") || title;

  return (
    <article>
      <PageMeta title={metaTitle} description={(metaDesc || "").slice(0, 160)} ogImage={resource.image_url || undefined} />
      <section className="pt-[140px] pb-12 px-6 relative" style={{ background: 'radial-gradient(circle at center, white 0%, #F3EDFD 70%)' }}>
        <PageContainer>
          <Link
            to={knowledgeHubPath}
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors mb-6"
          >
            <ArrowLeft className="w-4 h-4 flex-shrink-0" />
            <span>Terug</span>
          </Link>

          <div className="flex gap-10 items-start">
            <div className="flex-1 min-w-0 animate-fade-in">
              <h1 className="max-w-4xl [overflow-wrap:break-word] [hyphens:auto]" lang={currentLang} style={{ color: "#2A2334" }}>
                {title}
              </h1>
            </div>

            {/* Desktop form */}
            {hasForm && (
              <aside className="hidden lg:block animate-fade-in shrink-0 relative z-10" style={{ animationDelay: '200ms', width: '400px', marginBottom: '-1500px' }}>
                <div className="rounded-2xl px-0 py-8 bg-background shadow-xl" style={{ border: "1px solid hsl(var(--border))" }}>
                  <h4 className="mb-4 text-left text-2xl" style={{ color: "#2A2334", padding: "0 35px" }}>
                    {downloadLabel}
                  </h4>
                  <div ref={formRef} className="min-h-[200px]" />
                </div>
              </aside>
            )}
            {!hasForm && hasDownload && (
              <aside className="hidden lg:block animate-fade-in shrink-0 relative z-10" style={{ animationDelay: '200ms', width: '400px', marginBottom: '-1500px' }}>
                <div className="rounded-2xl p-8 bg-background shadow-xl text-center" style={{ border: "1px solid hsl(var(--border))" }}>
                  <h4 className="mb-4 text-2xl" style={{ color: "#2A2334" }}>
                    {downloadLabel}
                  </h4>
                  <a href={resource.download_url!} target="_blank" rel="noopener noreferrer">
                    <Button size="lg" className="w-full rounded-full">{currentLang === "en" ? "Download PDF" : currentLang === "de" ? "PDF herunterladen" : "Download PDF"}</Button>
                  </a>
                </div>
              </aside>
            )}
          </div>

          {/* Mobile form/download */}
          {hasForm && (
            <div className="lg:hidden mt-8 -mx-6 px-4">
              <div className="rounded-2xl px-0 py-8 bg-background shadow-xl" style={{ border: "1px solid hsl(var(--border))" }}>
                <h4 className="mb-4 text-left text-2xl" style={{ color: "#2A2334", padding: "0 24px" }}>
                  {downloadLabel}
                </h4>
                <div ref={mobileFormRef} className="min-h-[200px]" />
              </div>
            </div>
          )}
          {!hasForm && hasDownload && (
            <div className="lg:hidden mt-8">
              <a href={resource.download_url!} target="_blank" rel="noopener noreferrer">
                <Button size="lg" className="w-full rounded-full">{downloadLabel}</Button>
              </a>
            </div>
          )}
        </PageContainer>
      </section>

      {/* Image */}
      {resource.image_url && (
        <section className="pt-16 pb-0 px-6 relative">
          <PageContainer>
            <div className="max-w-4xl lg:max-w-[min(56rem,calc(100%-440px))]">

              <div className="mb-4 animate-fade-in" style={{ animationDelay: '300ms' }}>
                <div className="rounded-2xl overflow-hidden">
                  <img src={resource.image_url} alt={title} className="w-full h-auto" loading="lazy" />
                </div>
              </div>
            </div>
          </PageContainer>
        </section>
      )}


      {/* Content */}
      <section className="pt-8 pb-16 px-6 relative">
        <PageContainer>
          <div className="lg:max-w-[calc(100%-440px)]">
            <div className="prose max-w-none [&_p]:text-muted-foreground [&_li]:text-muted-foreground [&_strong]:font-normal [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-4 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-4 [&_li]:mb-1 [&_p]:whitespace-pre-line [&_p:empty]:block [&_p:empty]:h-6 [&_blockquote]:border-l-4 [&_blockquote]:border-primary [&_blockquote]:bg-[#F8F5FD] [&_blockquote]:rounded-xl [&_blockquote]:px-8 [&_blockquote]:py-6 [&_blockquote]:my-8 [&_blockquote]:not-italic [&_blockquote_p:last-child]:mb-0 [&_blockquote_a]:text-primary [&_blockquote_a]:underline [&_.text-center]:text-center">
              {contentParagraphs.length > 0 && (
                <div dangerouslySetInnerHTML={{ __html: contentParagraphs.join("\n") }} className="whitespace-pre-line [&_p]:text-muted-foreground [&_p:empty]:block [&_p:empty]:h-6 [&_h2]:mt-10 [&_h2]:mb-5 [&_h3]:mt-8 [&_h3]:mb-4 [&_h2:first-child]:mt-0 [&_h3:first-child]:mt-0" />
              )}
            </div>
          </div>
        </PageContainer>
      </section>
    </article>
  );
}
